import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Restaurant, RestaurantStats, Table, RestaurantContextType } from '../types';
import { useAuth } from './AuthContext';
import apiService from '../services/api';

const RestaurantContext = createContext<RestaurantContextType | null>(null);

export const useRestaurant = (): RestaurantContextType => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
};

interface RestaurantProviderProps {
  children: ReactNode;
}

export const RestaurantProvider: React.FC<RestaurantProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [stats, setStats] = useState<RestaurantStats | null>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.restaurante_id) {
      fetchRestaurantData();
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRestaurantData = async (): Promise<void> => {
    if (!user?.restaurante_id) return;

    try {
      setIsLoading(true);
      
      // Fetch restaurant details, stats, and tables in parallel
      const [restaurantData, statsData, tablesData] = await Promise.all([
        apiService.getRestaurantById(user.restaurante_id),
        apiService.getRestaurantStats(user.restaurante_id, 30), // Last 30 days
        apiService.getTables({ limit: 100 }) // Get all tables
      ]);

      setRestaurant(restaurantData);
      setStats(statsData);
      
      // Filter tables for this restaurant (API might return all tables if user is admin)
      const restaurantTables = tablesData.data.filter(
        table => table.restaurante_id === user.restaurante_id
      );
      setTables(restaurantTables);

    } catch (error: any) {
      console.error('Failed to fetch restaurant data:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateRestaurant = async (data: Partial<Restaurant>): Promise<void> => {
    if (!restaurant) return;

    try {
      setIsLoading(true);
      const updatedRestaurant = await apiService.updateRestaurant(restaurant.id, data);
      setRestaurant(updatedRestaurant);
    } catch (error) {
      console.error('Failed to update restaurant:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: RestaurantContextType = {
    restaurant,
    stats,
    tables,
    isLoading,
    fetchRestaurantData,
    updateRestaurant,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
